import { create } from 'zustand';
import { User, UserState } from '../types/user';
import { extractUserFromJWT, isValidJWT } from '../utils/jwt';
import {
  clearAuthData,
  getAuthToken,
  getCurrentStorageType,
  getUserInfo,
  setAuthToken,
  setUserInfo,
} from '../utils/storage';

/**
 * 권한 변경 이벤트 리스너 타입
 */
type PermissionChangeListener = (user: User | null) => void;

/**
 * 저장소에서 초기 상태를 로드합니다
 */
const loadInitialState = (): {
  accessToken: string | null;
  user: User | null;
} => {
  try {
    const token = getAuthToken();
    let user: User | null = null;
    let tokenValid = false;

    if (token) {
      try {
        tokenValid = isValidJWT(token);
        if (tokenValid) {
          user = getUserInfo();
        }
      } catch (jwtError) {
        console.warn('JWT 처리 중 오류:', jwtError);
      }
    }

    console.log(`🔄 [${getCurrentStorageType()}] 초기 상태 로드:`, {
      hasToken: !!token,
      hasUser: !!user,
      tokenValid,
    });

    return {
      accessToken: token,
      user,
    };
  } catch (error) {
    console.error('저장소에서 초기 상태 로드 실패:', error);
    return {
      accessToken: null,
      user: null,
    };
  }
};

/**
 * 권한 변경 이벤트 리스너들
 */
let permissionChangeListeners: PermissionChangeListener[] = [];

/**
 * 권한 해시를 생성합니다 (권한 변경 감지용)
 */
const createPermissionHash = (user: User | null): string => {
  return (
    user?.permissions
      ?.map((p) => p.name)
      .sort()
      .join(',') || ''
  );
};

/**
 * 권한 변경을 감지하고 리스너들에게 알립니다
 */
const notifyPermissionChange = (
  currentUser: User | null,
  newUser: User | null
) => {
  const currentHash = createPermissionHash(currentUser);
  const newHash = createPermissionHash(newUser);

  if (currentUser && currentHash !== newHash) {
    console.log('🔄 [UserStore] 권한 변경 감지됨 - 리스너들에게 알림', {
      이전권한수: currentUser.permissions?.length || 0,
      새권한수: newUser?.permissions?.length || 0,
    });

    // 권한 변경 리스너들에게 비동기 알림
    setTimeout(() => {
      permissionChangeListeners.forEach((listener) => {
        try {
          listener(newUser);
        } catch (error) {
          console.error('권한 변경 리스너 실행 오류:', error);
        }
      });
    }, 50);
  }
};

/**
 * 사용자 상태 관리 스토어
 */
export const useUserStore = create<UserState>()((set, get) => {
  const initialState = loadInitialState();

  return {
    user: initialState.user,
    accessToken: initialState.accessToken,

    /**
     * 사용자 정보를 설정합니다
     */
    setUser: (user: User) => {
      const currentUser = get().user;
      console.log('👤 사용자 정보 설정:', user?.email);

      // 권한 변경 감지 및 알림
      notifyPermissionChange(currentUser, user);

      set({ user });
      // JWT 기반이므로 별도 저장하지 않음
    },

    /**
     * 액세스 토큰을 설정합니다
     */
    setAccessToken: (token: string) => {
      console.log(
        '🔑 토큰 저장 시작:',
        token ? '***' + token.slice(-10) : null
      );

      if (token) {
        try {
          // JWT 유효성 검사
          if (!isValidJWT(token)) {
            console.error('❌ 유효하지 않은 JWT 토큰');
            return;
          }

          // 토큰 저장
          setAuthToken(token);

          // JWT에서 사용자 정보 추출하여 상태 업데이트
          const extractedUser = extractUserFromJWT(token);
          set({
            accessToken: token,
            user: extractedUser as unknown as User,
          });

          console.log('✅ 토큰 저장 및 사용자 정보 추출 완료:', {
            토큰저장: '완료',
            사용자정보: extractedUser ? '추출완료' : '추출실패',
            이메일: extractedUser?.email,
          });
        } catch (error) {
          console.error('❌ JWT 처리 중 오류:', error);
          // 토큰만 저장하고 사용자 정보는 나중에 추출
          setAuthToken(token);
          set({ accessToken: token, user: null });
        }
      } else {
        set({ accessToken: null, user: null });
      }
    },

    /**
     * 사용자 정보와 토큰을 모두 삭제합니다
     */
    clearUser: () => {
      console.log(`🗑️ [${getCurrentStorageType()}] 사용자 정보 및 토큰 삭제`);
      set({ user: null, accessToken: null });
      clearAuthData();
    },
  };
});

/**
 * 사용자 정보를 반환하는 셀렉터 훅
 */
export const useUser = () => useUserStore((state) => state.user);

/**
 * 액세스 토큰을 반환하는 셀렉터 훅
 */
export const useAccessToken = () => useUserStore((state) => state.accessToken);

/**
 * 권한 변경 이벤트 리스너를 등록합니다
 * @param listener 권한 변경 시 호출될 콜백 함수
 * @returns 리스너 제거 함수
 */
export const addPermissionChangeListener = (
  listener: PermissionChangeListener
) => {
  permissionChangeListeners.push(listener);
  return () => {
    permissionChangeListeners = permissionChangeListeners.filter(
      (l) => l !== listener
    );
  };
};

/**
 * 권한 변경 이벤트 리스너를 제거합니다
 * @param listener 제거할 콜백 함수
 */
export const removePermissionChangeListener = (
  listener: PermissionChangeListener
) => {
  permissionChangeListeners = permissionChangeListeners.filter(
    (l) => l !== listener
  );
};
